import type { AccumulationMode } from '../features/display/preferences'
import { decomposeChips, formatWholePoints } from '../../packages/domain/src/index'

export function accumulationChips(total: number): Array<{ value: number; label: string }> {
  return decomposeChips(total).chips.flatMap(({ denomination: value, count }) => {
    const label = value >= 1_000 ? `${value / 1_000}k` : String(value)
    return Array.from({ length: count }, () => ({ value, label }))
  })
}

type Props = {
  mode: AccumulationMode
  total: number | null
  maximum: number
  name: string
  since?: string
  freshness?: string
  singlePerson?: boolean
  surface?: 'scoreboard' | 'wall'
}

export function AccumulationDisplay({ mode, total, maximum, name, since, freshness, singlePerson, surface = 'scoreboard' }: Props) {
  if (mode === 'hidden') return null
  const safeTotal = total ?? 0
  const chips = accumulationChips(safeTotal)
  const ratio = Math.max(0, Math.min(1, safeTotal / Math.max(1, maximum)))
  const showLine = mode === 'line' || mode === 'both'
  const showChips = mode === 'chips' || mode === 'both'
  return <section className={`accumulation-display accumulation-${surface} ${surface === 'wall' ? 'wall-accumulation' : 'person-accumulation'}`} aria-label={`${name} accumulation`}>
    {showLine && (safeTotal > 0
      ? <figure className="accumulation-line"><svg viewBox="0 0 240 54" role="img" aria-label={`${name} banked ${formatWholePoints(total)} on shared scale ${formatWholePoints(maximum)}`} preserveAspectRatio="none"><path className="accumulation-baseline" d="M2 50 H238" /><path className="accumulation-path" d={`M2 50 C60 49 92 ${Math.round(48 - ratio * 18)} 132 ${Math.round(46 - ratio * 25)} S204 ${Math.round(45 - ratio * 38)} 238 ${Math.round(50 - ratio * 46)}`} /></svg></figure>
      : <div className="accumulation-placeholder" role="img" aria-label="No accumulated score yet"><span>No accumulated score yet</span></div>)}
    {showChips && <div className="accumulation-chips" aria-label={chips.length ? `${chips.length} accumulation chips` : 'No accumulation chips yet'}>
      {chips.map((chip, index) => <span key={`${chip.value}-${index}`} data-value={chip.value}>{chip.label}</span>)}
      {!chips.length && <small>{safeTotal > 0 ? 'First chip appears at 25' : 'Chips appear as points are banked'}</small>}
    </div>}
    <p className="accumulation-total"><strong>{formatWholePoints(total)} banked</strong>{since && <small>Since {since}</small>}{freshness && <small>Updated {freshness}</small>}</p>
    {singlePerson && showLine && <p className="scale-explanation">This person sets the shared line scale because they are the only person shown.</p>}
  </section>
}
